import { type Component, createMemo, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { useTalentPool } from "@/domains/company/talent-pool/context/talent-pool-provider";
import { pillVariant } from "../utils/pill-variant";

export type SavedPillProps = {
  freelancerProfileId: string;
};

/**
 * `SavedPill` — the card-footer badge that mirrors the freelancer's saved state.
 * It reads the provider entry and derives its label from the pure `pillVariant`
 * selector:
 *
 * - `none`      → renders nothing.
 * - `private`   → "♥ Nel Talent Pool · Privato".
 * - `shared`    → "♥ Nel Talent Pool · Condiviso".
 * - `generic`   → "♥ Nel Talent Pool" (post-409 window, no suffix — D5).
 * - `colleague` → "♥ Condiviso dal team" (read-only, saved by a teammate — D6).
 *
 * Reuses the card's `Badge` idiom (`light-primary` tint) so it reads as the
 * saved marker alongside the other footer badges.
 */
export const SavedPill: Component<SavedPillProps> = (props) => {
  const { t } = useI18n();
  const talentPool = useTalentPool();

  const variant = createMemo(() =>
    pillVariant(talentPool.entry(props.freelancerProfileId)),
  );

  return (
    <Show when={variant() !== "none"}>
      <Badge variant="light-primary" size="sm" class="gap-1">
        <i class="ph-fill ph-heart text-[10px]" aria-hidden="true" />
        <Switch>
          <Match when={variant() === "colleague"}>
            {t("company.talent_pool.pill.colleague")}
          </Match>
          <Match when={variant() === "private"}>
            {`${t("company.talent_pool.pill.base")} ${t(
              "company.talent_pool.pill.private_suffix",
            )}`}
          </Match>
          <Match when={variant() === "shared"}>
            {`${t("company.talent_pool.pill.base")} ${t(
              "company.talent_pool.pill.shared_suffix",
            )}`}
          </Match>
          <Match when={variant() === "generic"}>
            {t("company.talent_pool.pill.base")}
          </Match>
        </Switch>
      </Badge>
    </Show>
  );
};
